import {
  CATEGORY_LABEL,
  type ScoreEntity,
  WHERE_LIMITS,
  type WhereCategory,
  type WhereField,
  type WhereGroup,
  whereFieldName,
} from '@parcel/filters';
import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  cn,
} from '@parcel/ui';
import { ChevronDown, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import * as React from 'react';
import {
  type ActionProps,
  type FieldSelectorProps,
  type OperatorSelectorProps,
  RuleGroupBodyComponents,
  type RuleGroupProps,
  type ValueEditorProps,
  useRuleGroup,
} from 'react-querybuilder';
import { MenuSelect } from '../where-bar/menu-select.js';
import { RuleValueEditor } from '../where-bar/rule-value-editor.js';
import { chipValues } from '../where-bar/where-chip.js';
import type { WhereOptions } from '../where-bar/where-menu.js';
import { isUnpicked, ruleUnfinished } from './criteria-state.js';

/** What every control reads from the builder's context. */
export interface BuilderContext {
  entity: ScoreEntity;
  fields: readonly WhereField[];
  options: WhereOptions;
  /** Field mode: the card names the field, so the row shows no field picker. */
  lockedField?: string;
  mode: 'field' | 'relation' | 'custom';
  /** How many rules the condition's top level holds. */
  rootRules: number;
  /** The condition, for each row's lead word ("and" / "or"). */
  where: WhereGroup;
  disabled?: boolean;
}

const ctxOf = (context: unknown) => context as BuilderContext;

export type Node = { combinator?: string; rules?: Node[] };

/** "Where" on a group's first row, the group's own word ("and" / "or") on the next ones. */
function leadWord(path: readonly number[], root: WhereGroup): string {
  if (path[path.length - 1] === 0) return 'Where';
  let group: Node | undefined = root as Node;
  for (const i of path.slice(0, -1)) group = group?.rules?.[i];
  return group?.combinator === 'or' ? 'or' : 'and';
}

/** A card's own fields as one list ("City"); a custom condition's by category ("Project city"). */
export function FieldPicker({ value, handleOnChange, context, path }: FieldSelectorProps) {
  const ctx = ctxOf(context);
  const picked = !isUnpicked(value);
  // A new row opens on its field list: the field comes first.
  const [open, setOpen] = React.useState(() => !picked && !ctx.disabled);
  const [category, setCategory] = React.useState<WhereCategory | null>(null);
  if (ctx.lockedField) return null;
  const flat = ctx.mode !== 'custom';
  const current = picked ? ctx.fields.find((f) => f.key === value) : undefined;
  const categories = [...new Set(ctx.fields.map((f) => f.category))];
  const listed =
    flat || category === null ? ctx.fields : ctx.fields.filter((f) => f.category === category);
  const choose = (key: string) => {
    handleOnChange(key);
    setOpen(false);
    setCategory(null);
  };
  return (
    <>
      <span className="w-11 shrink-0 text-xs text-muted-foreground">
        {leadWord(path, ctx.where)}
      </span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setCategory(null);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="Attribute"
            disabled={ctx.disabled}
            className={cn(
              'h-8 max-w-[240px] shrink-0 justify-between gap-1.5 px-2.5 font-medium',
              !current && 'border-dashed font-normal text-muted-foreground',
            )}
          >
            <span className="truncate">
              {current
                ? flat
                  ? current.label
                  : whereFieldName(ctx.entity, current.key)
                : 'Choose a field'}
            </span>
            <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-0"
          align="start"
          // A picked field's value list has focus by now, and going back to this trigger would close it.
          onCloseAutoFocus={(event) => {
            if (document.activeElement && document.activeElement !== document.body) {
              event.preventDefault();
            }
          }}
        >
          <Command>
            <CommandList className="max-h-[min(320px,50vh)]">
              {!flat && category === null ? (
                <CommandGroup>
                  {categories.map((c) => (
                    <CommandItem key={c} value={`category:${c}`} onSelect={() => setCategory(c)}>
                      <span className="flex-1">{CATEGORY_LABEL[c]}</span>
                      <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : (
                <CommandGroup>
                  {!flat && category !== null ? (
                    <CommandItem value="back" onSelect={() => setCategory(null)}>
                      <ChevronLeft className="size-3.5 text-muted-foreground" aria-hidden />
                      {CATEGORY_LABEL[category]}
                    </CommandItem>
                  ) : null}
                  {listed.map((f) => (
                    <CommandItem
                      key={f.key}
                      value={`field:${f.key}`}
                      onSelect={() => choose(f.key)}
                    >
                      <span className="truncate">{f.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}

export function OperatorPicker({
  options,
  value,
  handleOnChange,
  context,
  field,
}: OperatorSelectorProps) {
  const ctx = ctxOf(context);
  if (isUnpicked(field)) return null;
  return (
    <MenuSelect
      label="Condition"
      value={value ?? ''}
      options={(options as ReadonlyArray<{ name: string; label: string }>).map((o) => ({
        value: o.name,
        label: o.label,
      }))}
      onChange={handleOnChange}
      disabled={ctx.disabled}
    />
  );
}

/** The value as a compact button; the editor opens beside it, as on the tables' chips. */
export function ValueEditorAdapter(props: ValueEditorProps) {
  const ctx = ctxOf(props.context);
  const empty = ruleUnfinished({
    field: props.field,
    operator: props.operator,
    value: props.value,
  });
  // A row that just got its field opens on its values.
  const [open, setOpen] = React.useState(() => empty && !ctx.disabled);
  const field = ctx.fields.find((f) => f.key === props.field);
  if (!field || props.operator === 'null' || props.operator === 'notNull') return null;
  const rule = { field: field.key, operator: props.operator, value: props.value };
  const shown = empty ? [] : chipValues(ctx.entity, rule, ctx.options).map((v) => v.label);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${field.label} value`}
          disabled={ctx.disabled}
          className={cn(
            'h-8 min-w-0 max-w-[280px] justify-between gap-1.5 px-2.5',
            empty && 'border-dashed font-normal text-muted-foreground',
          )}
        >
          <span className="truncate">{shown.length ? shown.join(', ') : 'Choose…'}</span>
          <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <RuleValueEditor
          dataset={ctx.entity}
          field={field}
          operator={props.operator}
          value={props.value}
          options={ctx.options}
          onChange={props.handleOnChange}
          onSubmit={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export function RemoveX({ handleOnClick, context, path }: ActionProps) {
  const ctx = ctxOf(context);
  if (ctx.disabled || ctx.mode === 'field') return null;
  // A Works on projects row keeps its last condition; the criterion's own remove takes it away.
  if (ctx.mode === 'relation' && path.length === 1 && ctx.rootRules <= 1) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      // Beside its own condition on a card row, so it never reads as the criterion's remove.
      className={cn('size-7 text-muted-foreground', ctx.mode === 'custom' && 'ml-auto')}
      aria-label="Remove condition"
      onClick={(e) => handleOnClick(e)}
    >
      <X className="size-4" aria-hidden />
    </Button>
  );
}

/** A small text action under the rows: "+ Condition", "+ Group". */
function AddText({ label, onClick }: { label: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      type="button"
      aria-label={`Add ${label.toLowerCase()}`}
      className="inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
      onClick={onClick}
    >
      <Plus className="size-3.5" aria-hidden />
      {label}
    </button>
  );
}

const COMBINATORS = [
  { value: 'and', label: 'AND' },
  { value: 'or', label: 'OR' },
];

/** How many groups can nest under the root: one less than the API's depth. */
export const MAX_NESTING = WHERE_LIMITS.depth - 1;

/**
 * One group of conditions: its rows, then "+ Condition". In a custom condition a group also says how its
 * rows combine (AND or OR, and Not) and can nest a group.
 */
export function ParcelRuleGroup(props: RuleGroupProps) {
  const rg = useRuleGroup(props);
  const ctx = ctxOf(props.context);
  const nested = rg.path.length > 0;
  const custom = ctx.mode === 'custom';
  // A negated group always shows its switch, so it never reads as positive.
  const showMode = custom && (nested || rg.ruleGroup.rules.length > 1 || Boolean(rg.ruleGroup.not));
  const canAdd = !ctx.disabled && ctx.mode !== 'field';
  const group = (
    <div
      className={cn(
        'flex flex-col gap-2',
        custom &&
          (nested ? 'rounded-md border bg-background p-2.5' : 'rounded-lg border bg-muted/20 p-3'),
      )}
      data-testid="rule-group"
      data-not={rg.ruleGroup.not ? 'true' : undefined}
    >
      {showMode ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MenuSelect
            label="Combine conditions with"
            value={rg.ruleGroup.combinator === 'or' ? 'or' : 'and'}
            options={COMBINATORS}
            onChange={rg.onCombinatorChange}
            disabled={ctx.disabled}
            className="h-7 text-xs text-foreground"
          />
          <span className="flex items-center gap-1.5">
            <Switch
              aria-label="Not"
              checked={Boolean(rg.ruleGroup.not)}
              onCheckedChange={rg.onNotToggleChange}
              disabled={ctx.disabled}
            />
            Not
          </span>
          {nested && !ctx.disabled ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto size-7 text-muted-foreground"
              aria-label="Remove group"
              onClick={(e) => rg.removeGroup(e)}
            >
              <X className="size-4" aria-hidden />
            </Button>
          ) : null}
        </div>
      ) : null}
      <RuleGroupBodyComponents {...rg} />
      {canAdd ? (
        <div className="flex items-center gap-1 pl-[3.25rem]">
          <AddText label="Condition" onClick={(e) => rg.addRule(e)} />
          {custom && rg.path.length < MAX_NESTING ? (
            <AddText label="Group" onClick={(e) => rg.addGroup(e)} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
  if (!nested) return group;
  return (
    <div className="flex items-start gap-2">
      <span className="w-11 shrink-0 pt-1.5 text-xs text-muted-foreground">
        {leadWord(rg.path, ctx.where)}
      </span>
      <div className="min-w-0 flex-1">{group}</div>
    </div>
  );
}
